import { Button } from '@ibsys/kit';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useUploadDictionaries, useAnonymizerConfig } from '@/entities/anonymizer-config';
import type { DictionariesConfig } from '@/entities/anonymizer-config';
import { FileButton } from '@/shared/ui';
import { ConfigFormLayout } from './ConfigFormLayout';

const DASH = '\u2014';
const COLON = ':';

const DICTIONARY_DOWNLOAD_URLS: Record<string, string> = {
  names: '/dictionaries/names.csv',
  patronymics: '/dictionaries/patronymics.csv',
  surnames: '/dictionaries/surnames.csv',
};

const noop = () => {
  // Словари загружаются по кнопкам, общего сохранения нет
};

type Props = {
  data: unknown;
  onUnsavedChange?: (dirty: boolean) => void;
};

export const DictionariesConfigForm: FC<Props> = ({ data }) => {
  const { t } = useTranslation('anonymizerConfig');
  const canEdit = useHasPermission('ADMIN_ANONYMIZE_CONFIG');
  const dictionaries = data as DictionariesConfig | undefined;
  const { mutate: upload, isPending } = useUploadDictionaries();
  const { refetch } = useAnonymizerConfig('fio/dictionaries');

  const handleUpload = (field: 'names' | 'surnames' | 'patronymics', file: File) => {
    const formData = new FormData();
    formData.append(field, file);
    upload(formData);
  };

  const handleDownload = useCallback((field: 'names' | 'surnames' | 'patronymics') => {
    const url = DICTIONARY_DOWNLOAD_URLS[field];
    const link = document.createElement('a');
    link.href = url;
    link.download = `${field}.csv`;
    link.click();
  }, []);

  const handleReset = () => {
    refetch();
  };

  return (
    <ConfigFormLayout
      title={t('classes.fioDictionaries')}
      isDirty={false}
      onSave={noop}
      onReset={handleReset}
      isSaving={isPending}
    >
      <div className="flex flex-col gap-6">
        <h3 className="text-lg font-medium">{t('fields.dictionaries')}</h3>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <span className="w-32 text-sm font-medium text-foreground">
              {t('fields.namesPlural')}
              {COLON}
            </span>
            <span className="flex-1 text-sm text-muted-foreground truncate">{dictionaries?.namesPath ?? DASH}</span>
            {canEdit && (
              <FileButton
                file={null}
                accept=".csv"
                label={t('fields.upload')}
                onChange={(file) => {
                  if (file) handleUpload('names', file);
                }}
              />
            )}
            <Button variant="outline" size="sm" onClick={() => handleDownload('names')}>
              {t('fields.download')}
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <span className="w-32 text-sm font-medium text-foreground">
              {t('fields.patronymicsPlural')}
              {COLON}
            </span>
            <span className="flex-1 text-sm text-muted-foreground truncate">
              {dictionaries?.patronymicsPath ?? DASH}
            </span>
            {canEdit && (
              <FileButton
                file={null}
                accept=".csv"
                label={t('fields.upload')}
                onChange={(file) => {
                  if (file) handleUpload('patronymics', file);
                }}
              />
            )}
            <Button variant="outline" size="sm" onClick={() => handleDownload('patronymics')}>
              {t('fields.download')}
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <span className="w-32 text-sm font-medium text-foreground">
              {t('fields.surnamesPlural')}
              {COLON}
            </span>
            <span className="flex-1 text-sm text-muted-foreground truncate">{dictionaries?.surnamesPath ?? DASH}</span>
            {canEdit && (
              <FileButton
                file={null}
                accept=".csv"
                label={t('fields.upload')}
                onChange={(file) => {
                  if (file) handleUpload('surnames', file);
                }}
              />
            )}
            <Button variant="outline" size="sm" onClick={() => handleDownload('surnames')}>
              {t('fields.download')}
            </Button>
          </div>
        </div>
      </div>
    </ConfigFormLayout>
  );
};
